// useConversation — React Query hook for a single conversation + its messages.
// Data comes from the backend GET /api/conversations/:id endpoint (server-side
// service-role fetch bypassing RLS). We use the backend rather than client Supabase
// because this is the same pattern we use for send, so the auth+data flow is uniform.
//
// Hosts the write-path mutations used by the Center page Composer and StatusPill:
//   useSendReply         — POST /api/conversations/:id/reply           (Gmail send, server-side)
//   useDraftReply        — POST /api/ai/draft/:id                      (Haiku drafter, Phase D2)
//   useRecordDraftSend   — POST /api/ai/drafts/:draftId/sent           (diff capture, Phase E-D)
//   useUpdateStatus      — PATCH /api/conversations/:id                (Phase 5a state machine)
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPatch } from '../api';
import type { ConversationStatus } from '@md/shared';
export interface ConversationRow {
  id: string;
  channel: string;
  external_id: string;
  brand: 'md' | 'hk';
  subject: string | null;
  customer_email: string | null;
  status: ConversationStatus;
  snoozed_until: string | null;
  status_changed_at: string;
  last_message_at: string;
  last_message_direction: 'inbound' | 'outbound' | null;
  created_at: string;
}
export interface MessageAttachment {
  id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  url: string | null;
}
export interface MessageRow {
  id: string;
  direction: 'inbound' | 'outbound';
  sender_name: string | null;
  sender_identifier: string;
  body: string;
  body_raw: string | null;
  sent_at: string;
  external_message_id: string | null;
  /** Polish #18 — attachments bound to this message (inbound or outbound). */
  attachments?: MessageAttachment[];
}
export interface ConversationResponse {
  conversation: ConversationRow;
  messages: MessageRow[];
}
export function useConversation(id: string | undefined) {
  return useQuery<ConversationResponse>({
    queryKey: ['conversation', id],
    queryFn: () => apiGet<ConversationResponse>(`/api/conversations/${id}`),
    enabled: !!id,
    staleTime: 10_000,
  });
}
export interface ReplyResponse {
  ok: true;
  messageId: string | null;
  externalMessageId: string | null;
}
export function useSendReply(conversationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    ReplyResponse,
    Error,
    { body: string; attachment_ids?: string[] }
  >({
    mutationFn: (vars) =>
      apiPost<ReplyResponse, { body: string; attachment_ids?: string[] }>(
        `/api/conversations/${conversationId}/reply`,
        {
          body: vars.body,
          ...(vars.attachment_ids && vars.attachment_ids.length > 0
            ? { attachment_ids: vars.attachment_ids }
            : {}),
        },
      ),
    onSuccess: () => {
      // Invalidate the single-conversation view + the Standup list so status changes show up.
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      // Polish #18 — composer attachments become message attachments after send;
      // refresh proofs so the Mark-as-proof button lights up for the new message.
      void qc.invalidateQueries({ queryKey: ['proofs', conversationId] });
    },
  });
}
// ─── Haiku drafter (Phase D2 / E-B / E-D) ───────────────────────────────────
// POST /api/ai/draft/:id runs the full draft pipeline: pulls conversation +
// messages, assembles the system prompt, maps the thread onto alternating
// Anthropic turns, and calls Haiku 4.5. Response matches the DraftOutcome
// type in packages/backend/src/ai/draft.ts.
//
// Phase E-D: response now includes `draftId` — the Composer passes this through
// to useRecordDraftSend after a successful send so we can capture the edit diff.
//
// We deliberately do NOT invalidate any queries on success — drafting is a
// read of the AI, not a mutation of conversation state. The Composer pulls
// res.draft and stuffs it straight into the textarea.
export interface DraftResponse {
  ok: true;
  dryRun: boolean;
  draftId?: string;
  draft: string;
  model: string;
  usage: { input_tokens: number; output_tokens: number };
  stop_reason: string | null;
  elapsedMs: number;
  turnCount?: number;
}
export function useDraftReply(conversationId: string | undefined) {
  return useMutation<DraftResponse, Error, void>({
    mutationFn: () =>
      apiPost<DraftResponse, Record<string, never>>(
        `/api/ai/draft/${conversationId}`,
        {},
      ),
  });
}
// ─── Diff capture (Phase E-D) ───────────────────────────────────────────────
// Called by the Composer AFTER a successful send, if there's a draftId in
// flight. Fire-and-forget from the UI's perspective — any failure here is
// logged server-side but doesn't affect the user. The whole point is to
// silently build a corpus of draft-vs-sent pairs for prompt tuning.
export interface RecordDraftSendResponse {
  ok: true;
  editId: string | null;
  charDelta: number;
}
export function useRecordDraftSend() {
  return useMutation<
    RecordDraftSendResponse,
    Error,
    { draftId: string; sentText: string }
  >({
    mutationFn: (vars) =>
      apiPost<RecordDraftSendResponse, { sent_text: string }>(
        `/api/ai/drafts/${vars.draftId}/sent`,
        { sent_text: vars.sentText },
      ),
  });
}
// ─── Status transitions (Phase 5a) ──────────────────────────────────────────
// PATCH /api/conversations/:id — single endpoint handles all five statuses.
// Backend validates that snoozed_until is a future timestamp iff status='snoozed'.
// On success, invalidates both the single conversation and the Standup list
// so bucket counts update immediately.
export interface UpdateStatusResponse {
  ok: true;
  conversation: {
    id: string;
    status: ConversationStatus;
    snoozed_until: string | null;
    status_changed_at: string;
    updated_at: string;
  };
}
export interface UpdateStatusVars {
  id: string;
  status: ConversationStatus;
  /** ISO timestamp; required iff status='snoozed', ignored otherwise. */
  snoozed_until?: string | null;
}
export function useUpdateStatus() {
  const qc = useQueryClient();
  return useMutation<UpdateStatusResponse, Error, UpdateStatusVars>({
    mutationFn: (vars) =>
      apiPatch<UpdateStatusResponse, { status: ConversationStatus; snoozed_until?: string | null }>(
        `/api/conversations/${vars.id}`,
        {
          status: vars.status,
          snoozed_until: vars.status === 'snoozed' ? (vars.snoozed_until ?? null) : null,
        },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['conversation', vars.id] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}
